import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Sector,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { makeStyles } from "@mui/styles";
import Colors from "../../theme/colors.json";
import { MenuItem, Select } from "@mui/material";
import { INITIAL_THEME } from "../../theme";
import { pushNotification } from "../../redux/actions";
import CONSTANTS, {
  Chart_Errors,
  USER_GROUPS,
} from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { getProposalCounts } from "../../services/ChartService";
import NoData from "./NoData";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  responsiveContainer: {
    width: "100%",
    height: "100%",
    fontSize: theme.fontSizes.SMALL,
    // paddingRight: "2rem",
    paddingBottom: "2rem",
  },
  filterContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "3%",
  },
  filtersSection: {
    display: "flex",
    gap: "3%",
    // justifyContent: "space-around",
    // width: "50%",
  },
  selectStyles: {
    color: theme.palette.primary.main,
    fontSize: theme.fontSizes.SMALL,
    height: "1.6rem",
    minWidth: "5rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      fill: `${theme.palette.primary.main} !important`,
    },
  },

  graphHeading: {
    fontSize: theme.fontSizes.LARGE,
    fontWeight: 600,
    // width: "10%",
    // color: theme.palette.secondary.main,
  },

  selectRoot: {
    border: "unset",
    borderColor: theme.palette.primary.main,
  },

  icon: {
    fill: theme.palette.primary.main,
  },
  userSelect: {
    color: theme.palette.primary.main,
    fontSize: theme.fontSizes.SMALL,
    height: "1.6rem",
    maxWidth: "7rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      fill: `${theme.palette.primary.main} !important`,
    },
  },
  legendProposalStatus: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
}));

const data = [
  { name: "Closed Won", value: 400 },
  { name: "Closed Lost", value: 300 },
  { name: "In-Process", value: 300 },
];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  value,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {value}
      {/* {`${(percent * 100).toFixed(0)}%`} */}
    </text>
  );
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const ProposalChart = ({ years, months, allUsers }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  const { userId } = useSelector((state: any) => ({
    userId: state.userId,
  }));

  const [selectedYear, setSelectedYear] = useState(
    years[0]?.value || new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedUser, setSelectedUser] = useState(
    userGroup === USER_GROUPS.ADMIN ? "all" : userId
  );
  const [chartData, setChartData] = useState<any>([
    { name: "Closed Won", value: 0 },
    { name: "Closed Lost", value: 0 },
    { name: "In-Process", value: 0 },
  ]);

  const nochartData = [
    { name: "Closed Won", value: 0 },
    { name: "Closed Lost", value: 0 },
    { name: "In-Process", value: 0 },
    { name: "No Proposals", value: 1 },
  ];

  const [isLoading, setIsLoading] = useState(false);

  const handlegetProposalCounts = async () => {
    setIsLoading(true);
    const response = await getProposalCounts(
      selectedMonth,
      selectedYear,
      selectedUser
    );
    if (response?.success) {
      setChartData([
        { name: "Closed Won", value: parseInt(response?.data[0]?.closedWon) },
        { name: "Closed Lost", value: parseInt(response?.data[0]?.closedLost) },
        { name: "In-Process", value: parseInt(response?.data[0]?.inProcess) },
      ]);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.SUCCESS,
      //   })
      // );
    } else {
      setChartData([]);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.ERROR,
      //   })
      // );
    }
  };

  useEffect(() => {
    handlegetProposalCounts();
  }, [selectedMonth, selectedYear, selectedUser]);

  return (
    <>
      <div className={classes.filterContainer}>
        <div className={classes.graphHeading}>Proposals</div>
        <div className={classes.filtersSection}>
          {userGroup === USER_GROUPS.ADMIN && (
            <Select
              size="small"
              className={classes.userSelect}
              variant="outlined"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              {allUsers?.map((item, index) => {
                return (
                  <MenuItem key={index} value={item?.userId}>
                    {item?.fullName}
                  </MenuItem>
                );
              })}
            </Select>
          )}
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>

      {chartData?.length > 0 && !isLoading && (
        <ResponsiveContainer className={classes.responsiveContainer}>
          {chartData.every((entry) => entry.value === 0) ? (
            <PieChart width={400} height={400}>
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                formatter={(value, entry, index) => {
                  const { payload }: any = entry;
                  if (index !== 3) {
                    return (
                      <span>
                        {payload?.value}{" "}
                        <span className={classes.legendProposalStatus}>
                          {payload?.name}
                        </span>
                      </span>
                    );
                  } else {
                    return (
                      <span className={classes.legendProposalStatus}>
                        No Proposals Found
                      </span>
                    );
                  }
                }}
              />
              <Pie
                data={nochartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                // label={renderCustomizedLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                onClick={() =>
                  navigate(`${process.env.PUBLIC_URL}/proposalList`)
                }
              >
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              {/* <Tooltip /> */}
            </PieChart>
          ) : (
            <PieChart width={400} height={400}>
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                formatter={(value, entry, index) => {
                  const { payload }: any = entry;
                  return (
                    <span>
                      {payload?.value}{" "}
                      <span className={classes.legendProposalStatus}>
                        {payload?.name}
                      </span>
                    </span>
                  );
                }}
              />
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                onClick={() =>
                  navigate(`${process.env.PUBLIC_URL}/proposalList`)
                }
              >
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          )}
        </ResponsiveContainer>
      )}

      {chartData?.length === 0 && !isLoading && (
        <NoData errorLabel={Chart_Errors.proposal_error} />
      )}
    </>
  );
};

export default ProposalChart;
